<template>
  <div class="market-chart-holder">
    <div class="market-chart-copyright" @click="$router.push('/terms')">
      Copyright (с) 2019 TradingView, Inc.
    </div>
    <chart-menu
      :chartType="chartType"
      :ticker="String(ticker).toUpperCase()"
      :comparables="comparables"
      :selected="tickersSelected"
      @tickerclick="toggleTickerCompare"
      @chartTypeChange="chartTypeChange"
    />
    <div ref="marketChart" class="market-chart"></div>
  </div>
</template>

<script>
import { isObject, isNumber, throttle } from 'lodash'
import moment from 'moment-timezone'
import getNextDate from './getNextDate'
import getChartOptions from './getChartOptions'
import addChartData from './addChartData'
import { createChart, PriceScaleMode } from 'lightweight-charts'
import { mapState } from 'vuex'
import ChartMenu from '../ChartMenu'

const maxHeight = 300
const timeZone = -new Date().getTimezoneOffset() * 60
const INFINITE_LOAD_TRIGGER = 50

export default {
  data() {
    const timeInterval = '15m'
    return {
      currentBar: {
        open: null,
        high: null,
        low: null,
        close: null,
        time: this.getNextDate(timeInterval, timeZone),
      },
      currentTime: this.getNextDate(timeInterval, timeZone),
      loading: false,
      chartType: 'candles',
      tickersSelected: [],
      timeInterval,
      loadedUntil: null,
    }
  },
  computed: {
    ...mapState('layout', {
      themeBlack: (state) => state.themeBlack,
    }),
    ...mapState('market', {
      ticker: (state) => state.ticker,
      currentPrice: (state) => state.currentPrice,
      platform: (state) => state.platform,
    }),
    comparables() {
      return [
        {
          title_en: this.ticker,
          title_ru: this.ticker,
          id: -1,
          apiName: this.ticker,
        },
      ]
        .concat(this.$store.state.ticker.chartComparables.slice(0, 3))
        .map((a) => ({ ...a, ticker: String(a.ticker).toUpperCase() }))
    },
  },
  watch: {
    ticker() {
      this.unsubscribeAll()
      this.resetChart()
      this.prepareSockets()
    },
    platform() {
      this.unsubscribeAll()
      this.resetChart()
      this.prepareSockets()
    },
    currentPrice(val) {
      this.mergeTickToBar(val)
    },
    themeBlack() {
      this.chart.applyOptions({
        layout: {
          backgroundColor: this.themeBlack ? '#131722' : '#FFFFFF',
          textColor: this.themeBlack ? '#d1d4dc' : '#191919',
        },
        grid: {
          vertLines: {
            color: this.themeBlack
              ? 'rgba(42, 46, 57, 0.6)'
              : 'rgba(42, 46, 57, 0.3)',
          },
          horzLines: {
            color: this.themeBlack
              ? 'rgba(42, 46, 57, 0.6)'
              : 'rgba(42, 46, 57, 0.3)',
          },
        },
      })
    },
  },
  beforeDestroy() {
    this.unsubscribeAll()
    this.destroyChart()
  },
  mounted() {
    this.resetChart()
    this.prepareSockets()
  },
  beforeRouteLeave() {
    this.unsubscribeAll()
    this.destroyChart()
  },
  beforeUnmount() {
    this.unsubscribeAll()
    this.destroyChart()
  },
  methods: {
    resetChart() {
      this.createChart()
      this.getDataFromServer(this.$store.state.market.ticker)
      this.subscribeToRangeChange()
    },
    prepareSockets() {
      this.unsubscribeMutation = this.$store.subscribe((mutation) => {
        if (mutation.type !== 'request/socket/screenerTick') return
        const { ticker, c } = mutation.payload

        try {
          this.lineSeries[ticker].update({
            time: this.currentBar.time,
            value: c,
          })
        } catch (error) {
          console.log(
            `Update of lineSeries was unsuccessful, ticker='${ticker}', ${this.currentBar.time}, ${this.currentBar.time}`,
            error
          )
        }
      })
    },
    updateInterval(v) {
      this.timeInterval = v
      while (this.tickersSelected.length > 0) {
        this.toggleTickerCompare(this.tickersSelected[0])
      }
      this.resetChart()
    },
    unsubscribeAll() {
      // remove old series
      while (
        isObject(this.lineSeries) &&
        Object.keys(this.lineSeries).length > 0
      ) {
        this.toggleTickerCompare(
          Object.keys(this.lineSeries)[0],
          undefined,
          true
        ) // remove lineSeries
      }
      this.unsubscribeMutation && this.unsubscribeMutation()
    },
    getDataFromServer(ticker, color) {
      if (this.chartType == 'candles' && this.loading) return
      if (!ticker.length > 0) return
      this.loading = true
      let otherMarket = false
      if (ticker.indexOf('/') > -1) otherMarket = true
      this.$store
        .dispatch('request/getGraphData', {
          market: otherMarket ? ticker.split('/')[0] : this.platform,
          ticker: otherMarket ? ticker.split('/')[1] : ticker,
          period: this.timeInterval,
        })
        .then(({ data }) => {
          addChartData(this, data, timeZone, ticker, color)
        })
        .finally(() => {
          this.loading = false
        })
    },
    subscribeToRangeChange() {
      if (this.throttledVisibleTimeRangeChange)
        delete this.throttledVisibleTimeRangeChange

      this.throttledVisibleTimeRangeChange = throttle(
        (timeRange) => {
          if (null === this.minTime) return
          const window = timeRange.to - timeRange.from
          const DEFAULT_WINDOW = 83.5 // magic value from reality

          // if left candle index is less than visible window size
          if (
            timeRange.from <
            (INFINITE_LOAD_TRIGGER * window) / DEFAULT_WINDOW
          ) {
            this.loadMoreTicks()
          }
        },
        1000,
        { leading: true, trailing: false }
      )
      this.chart
        .timeScale()
        .subscribeVisibleLogicalRangeChange(
          this.throttledVisibleTimeRangeChange
        )
    },
    getNextDate,
    loadMoreTicks() {
      const tickers =
        'lines' === this.chartType
          ? Object.keys(this.lineSeries).map((t) => [t, 'lines'])
          : [[`${this.platform}/${this.ticker}`, 'candles']]

      if (null === this.loadedUntil) return // do not refresh ticks until get it first time
      const OHLCs = Array.from(tickers).map(([tickerString, seriesType]) => {
        const [market, ticker] = tickerString.includes('/')
          ? tickerString.split('/')
          : [this.platform, tickerString]

        return this.$store
          .dispatch('request/getGraphData', {
            market,
            ticker: 'lines' === seriesType ? ticker : this.ticker,
            period: this.timeInterval,
            from: new Date((this.loadedUntil - timeZone) * 1000),
          })
          .then(({ data }) => {
            return {
              data,
              tickerString: 'lines' === seriesType ? tickerString : null,
              seriesType,
            }
          })
          .catch((err) => {
            console.log(`Can't load more graph data`, err)
          })
      })

      let loadedUntil = this.loadedUntil

      Promise.all(OHLCs)
        .then((results) => {
          results.map(({ data, tickerString, seriesType }) => {
            if (!data || data.length < 1) return
            /** @type {Array} */
            let dataSorted
            if ('lines' === seriesType) {
              dataSorted = data
                .map((el) => ({
                  value: el.close,
                  time: Math.trunc(moment(el.time).valueOf() / 1000 + timeZone),
                }))
                .sort((a, b) => a.time - b.time)
              while (
                dataSorted[dataSorted.length - 1] &&
                dataSorted[dataSorted.length - 1].time >=
                  this.lineSeries[tickerString]._myData[0].time
              ) {
                dataSorted.pop()
              }
              this.lineSeries[tickerString].setData([
                ...dataSorted,
                ...this.lineSeries[tickerString]._myData,
              ])
              this.lineSeries[tickerString]._myData = [
                ...dataSorted,
                ...this.lineSeries[tickerString]._myData,
              ]
              loadedUntil = this.lineSeries[tickerString]._myData[0].time
            } else {
              dataSorted = data
                .map((el) => ({
                  ...el,
                  time: Math.trunc(moment(el.time).valueOf() / 1000 + timeZone),
                }))
                .sort((a, b) => a.time - b.time)
              while (
                dataSorted[dataSorted.length - 1] &&
                dataSorted[dataSorted.length - 1].time >=
                  this.candleSeries._myData[0].time
              ) {
                dataSorted.pop()
              }
              this.candleSeries.setData([
                ...dataSorted,
                ...this.candleSeries._myData,
              ])
              this.candleSeries._myData = [
                ...dataSorted,
                ...this.candleSeries._myData,
              ]
              loadedUntil = this.candleSeries._myData[0].time
            }
          })

          this.loadedUntil = loadedUntil
        })
        .finally(() => {
          this.loading = false
        })
    },
    mergeTickToBar(price) {
      if (this.currentBar.open === null) {
        this.currentBar.open = price
        this.currentBar.high = price
        this.currentBar.low = price
        this.currentBar.close = price
      } else {
        this.currentBar.close = price
        this.currentBar.high = Math.max(this.currentBar.high, price)
        this.currentBar.low = Math.min(this.currentBar.low, price)
      }
      if (
        this.currentBar.low &&
        this.currentBar.high &&
        this.currentBar.open &&
        this.currentBar.close &&
        this.chartType === 'candles' &&
        this.candleSeries
      )
        this.candleSeries.update(this.currentBar)

      if (this.chartType === 'lines' && this.lineSeries) {
        this.lineSeries[this.ticker].update({
          value: this.currentBar.close,
          time: this.currentBar.time,
        })
      }

      if (this.chartType === 'candles' && this.candleSeries)
        this.candleSeries.applyOptions({
          priceFormat: {
            type: 'custom',
            formatter: (price) => {
              return isNumber(price)
                ? price.toFixed(this.$store.state.market.digitsAfterDot)
                : price.toString()
            },
          },
        })
    },
    changeCurrentTime() {
      if (
        this.currentBar.time === this.getNextDate(this.timeInterval, timeZone)
      )
        return
      this.currentIndex++

      this.currentBar = {
        open: null,
        high: null,
        low: null,
        close: null,
        time: this.getNextDate(this.timeInterval, timeZone),
      }
    },
    destroyChart() {
      if (this.chart) {
        this.chart.remove()
        delete this.chart
      }
      if (this.intervalId) {
        clearInterval(this.intervalId)
        this.intervalId = null
      }
      if (this.candleSeries) delete this.candleSeries
      if (this.lineSeries) delete this.lineSeries
      this.$refs.marketChart.innerHTML = ''
      //TODO
      // unsubscribe visible time range
    },
    createChart() {
      if (!this.lineSeries) this.lineSeries = {}
      this.destroyChart()
      this.$refs.marketChart.innerHTML = ''
      this.chart = createChart(
        this.$refs.marketChart,
        getChartOptions(this.themeBlack, this.$refs.marketChart.offsetWidth)
      )

      this.changeCurrentTime()
      this.intervalId = setInterval(this.changeCurrentTime, 1000)

      const that = this
      this.throttledResize = throttle(
        () =>
          that.chart &&
          that.chart.resize(that.$refs.marketChart.offsetWidth, maxHeight),
        50
      )

      window.addEventListener('resize', this.throttledResize)

      // tooltip setup
      this.toolTip = document.createElement('div')
      this.toolTip.className = 'floating-tooltip-2'
      this.$refs.marketChart.appendChild(this.toolTip)

      this.chart.subscribeCrosshairMove((param) => {
        this.updateTooltip(param)
      })
    },
    updateTooltip(param) {
      if (
        param.point === undefined ||
        !param.time ||
        param.point.x < 0 ||
        param.point.x > this.$refs.marketChart.clientWidth ||
        param.point.y < 0 ||
        param.point.y > this.$refs.marketChart.clientHeight
      ) {
        this.toolTip.style.display = 'none'
      } else {
        let coordinate = null
        const date = param.time - 3 * 60 * 60
        this.toolTip.style.display = 'block'

        if (this.chartType === 'candles') {
          const price = param.seriesPrices.get(this.candleSeries)
          coordinate = this.candleSeries.priceToCoordinate(price.close)
          this.toolTip.innerHTML = `<div style="color: #00a6cb; font-size: ${
            this.$store.state.market.companyName.length > 10 ? 0.7 : 0.9
          }rem;">${this.$store.state.market.companyName}</div>
            <div>
              Open: ${price.open}<br>
              High: ${price.high}<br>
              Low: ${price.low}<br>
              Close: ${price.close}
            </div>
            <div>${moment(date * 1000).format(
              this.timeInterval == '1d' ? 'YY-MM-DD' : 'YY-MM-DD HH:mm'
            )}</div>`
        } else {
          let message = ''

          message += Object.keys(this.lineSeries)
            .map((t) => {
              const price = param.seriesPrices.get(this.lineSeries[t])
              if (coordinate === null)
                coordinate = this.lineSeries[t].priceToCoordinate(price)
              else
                coordinate =
                  (coordinate + this.lineSeries[t].priceToCoordinate(price)) / 2
              t = this.comparables.filter(
                (ls) => ls.apiName == t || `${this.platform}/${ls.apiName}` == t
              )[0]
              return `<div>${
                this.$i18n.locale == 'en' && t.title_en
                  ? t.title_en
                  : t.title_ru
              }: ${price ? price : '--'}</div>`
            })
            .join('')
          message += `<div>${moment(date * 1000).format(
            this.timeInterval == '1d' ? 'YY-MM-DD' : 'YY-MM-DD HH:mm'
          )}</div>`

          this.toolTip.innerHTML = message
        }

        const toolTipWidth = this.toolTip.offsetWidth || 120,
          toolTipHeight = this.toolTip.offsetHeight || 135,
          toolTipMargin = 15

        var shiftedCoordinate = param.point.x - 50
        if (coordinate === null) {
          return
        }
        shiftedCoordinate = Math.max(
          0,
          Math.min(
            this.$refs.marketChart.clientWidth - toolTipWidth,
            shiftedCoordinate
          )
        )
        var coordinateY =
          coordinate - toolTipHeight - toolTipMargin > 0
            ? coordinate - toolTipHeight - toolTipMargin
            : Math.max(
                0,
                Math.min(
                  this.$refs.marketChart.clientHeight -
                    toolTipHeight -
                    toolTipMargin,
                  coordinate + toolTipMargin
                )
              )
        this.toolTip.style.left = shiftedCoordinate + 'px'
        this.toolTip.style.top = coordinateY + 'px'
      }
    },
    toggleTickerCompare(ticker, color, forceUnsubscribe = false) {
      if (String(this.ticker).toUpperCase() == ticker && !forceUnsubscribe)
        return

      let otherMarket = false
      if (ticker.indexOf('/') > -1) otherMarket = true
      const fullTickerName = otherMarket ? ticker : `${this.platform}/${ticker}`

      if (this.tickersSelected.indexOf(ticker) > -1 || forceUnsubscribe) {
        this.$store.dispatch(
          `request/socket/leave`, // subscribe
          {
            ticker: otherMarket ? ticker.split('/')[1] : ticker,
            ticksOnly: true,
            market: otherMarket ? ticker.split('/')[0] : this.platform,
          }
        )
        this.tickersSelected.splice(
          this.tickersSelected.indexOf(fullTickerName || ticker),
          1
        )
        this.chart.removeSeries(
          this.lineSeries[fullTickerName] || this.lineSeries[ticker]
        )
        if (this.lineSeries[fullTickerName])
          delete this.lineSeries[fullTickerName]
        else delete this.lineSeries[ticker]
        if (Object.entries(this.lineSeries).length === 1)
          this.chart.priceScale('right').applyOptions({
            mode: PriceScaleMode.Normal,
          })
      } else {
        this.$store.dispatch(
          `request/socket/subscribe`, // subscribe
          {
            ticker: otherMarket ? ticker.split('/')[1] : ticker,
            ticksOnly: true,
            market: otherMarket
              ? ticker.split('/')[0]
              : this.$store.state.market.platform,
          }
        )
        this.tickersSelected.push(ticker)
        this.getDataFromServer(otherMarket ? ticker : fullTickerName, color)
      }
    },
    chartTypeChange(to) {
      this.chartType = to
      this.resetChart()
      if (
        this.chartType == 'lines' &&
        this.lineSeries &&
        Object.entries(this.lineSeries).length === 1
      )
        this.chart.priceScale('right').applyOptions({
          mode: PriceScaleMode.Normal,
        })
    },
  },
  components: {
    'chart-menu': ChartMenu,
  },
}
</script>

<style lang="scss">
.floating-tooltip-2 {
  position: absolute;
  display: none;
  padding: 8px;
  box-sizing: border-box;
  font-size: 0.9em;
  line-height: 1.4em;
  color: black;
  background-color: rgba(255, 255, 255, 1);
  text-align: left;
  z-index: 1000;
  top: 12px;
  left: 12px;
  pointer-events: none;
  border: 1px solid #00a6cb;
  border-radius: 2px;
}
#app[data-theme='dark'] .floating-tooltip-2 {
  color: white;
  background-color: black;
}
.market-chart {
  &-holder {
    position: relative;
  }
}
</style>
